/**
 * @deprecated Import SearchInput from "@/components/ui/search-input" instead.
 * This file exists only for backward compatibility.
 * 
 * The unified SearchInput now lives in src/components/ui/search-input.tsx
 */

import { useCallback, useEffect, useState, forwardRef } from "react";
import { SearchInput as BaseSearchInput, SearchInputProps as BaseSearchInputProps } from "@/components/ui/search-input";

// Legacy API that accepts value/onChange as (value: string) => void
export interface SearchInputProps extends Omit<BaseSearchInputProps, 'onChange' | 'value'> {
  /** Current search value */
  value: string;
  /** Callback when value changes - receives string directly */
  onChange: (value: string) => void;
  /** Input className overrides (legacy prop, mapped to className) */
  inputClassName?: string;
  /** Container className for positioning/layout */
  containerClassName?: string;
}

/**
 * @deprecated Use SearchInput from "@/components/ui/search-input" instead.
 * This wrapper maintains backward compatibility with the value/onChange(string) API.
 */
export const SearchInput = forwardRef<HTMLInputElement, SearchInputProps>(({
  value,
  onChange,
  inputClassName,
  className,
  containerClassName,
  debounceMs = 0,
  ...props
}, ref) => {
  const [localValue, setLocalValue] = useState(value);

  // Sync local value with external value
  useEffect(() => {
    setLocalValue(value);
  }, [value]);

  // Debounced onChange handler
  useEffect(() => {
    if (debounceMs === 0) return;
    
    const timer = setTimeout(() => {
      if (localValue !== value) {
        onChange(localValue);
      }
    }, debounceMs);

    return () => clearTimeout(timer);
  }, [localValue, debounceMs, onChange, value]);

  const handleChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = e.target.value;
    setLocalValue(newValue);
    
    // Immediate callback if no debounce
    if (debounceMs === 0) {
      onChange(newValue);
    }
  }, [debounceMs, onChange]);

  return (
    <BaseSearchInput
      ref={ref}
      value={debounceMs > 0 ? localValue : value}
      onChange={handleChange}
      className={inputClassName || className}
      containerClassName={containerClassName}
      showIcon={props.showIcon ?? true}
      {...props}
    />
  );
});

SearchInput.displayName = "SearchInput";
